import React, { useState, useEffect } from 'react';
import { toast } from 'react-toastify';
import InputField from '../../../components/InputField/InputField';
import TextAreaField from '../../../components/TextAreaField/TextAreaField';
import ButtonGeneral from '../../../components/buttons/ButtonGeneral';
import SquareCheckbox from '../../../components/SquareCheckbox/SquareCheckbox';
import useHandleErrorFields from '../../../hooks/useHandleErrorFields';
import useOfficialPublicationService from '../../../app/services/hooks/useOfficialPublicationService';
import usePermissions from '../../../hooks/usePermissions';
import { StagesData } from '../../../app/services/hooks/useBrandRegistrationStagesService';
import { ServiceResponse } from '../../../app/services/shared/interfaces';
import { safeRedirectUrl } from '../../Organization/types';

interface OfficialPublicationStepProps {
    brandRegistrationId: number;
    stagesData: StagesData | null;
    isEditable: boolean;
    onUpdate: () => void;
}

const OfficialPublicationStep: React.FC<OfficialPublicationStepProps> = ({
    brandRegistrationId,
    stagesData,
    isEditable,
    onUpdate
}) => {
    const { can } = usePermissions();
    const { fieldErrorMessage, onFocusRemove, setErrors } = useHandleErrorFields();
    const {
        getFormalExaminationId,
        getOfficialPublication,
        createOfficialPublication,
        updateOfficialPublication,
        sendPaymentEmail,
        createFlowPayment,
        getInapiDataForAutofill,
        fetchingGetFormalExaminationId,
        fetchingGetOfficialPublication,
        fetchingCreateOfficialPublication,
        fetchingUpdateOfficialPublication,
        fetchingSendPaymentEmail,
        fetchingCreateFlowPayment,
        fetchingInapiAutofill
    } = useOfficialPublicationService();

    const [formalExaminationId, setFormalExaminationId] = useState<number | null>(null);
    const [publicationData, setPublicationData] = useState<{
        id: number;
        publication_fee: number;
        payment_status: string;
        days_remaining: number;
        payment_date?: string;
        opposition_status?: string;
        publication_date?: string;
        opposition_period_start?: string;
        opposition_period_end?: string;
        observations?: string;
    } | null>(null);
    const [showCreateForm, setShowCreateForm] = useState(false);
    const [showEditBasicInfo, setShowEditBasicInfo] = useState(false);
    const [showPaymentForm, setShowPaymentForm] = useState(false);
    
    const [createFormData, setCreateFormData] = useState({
        publication_fee: '',
        resolution_notification_date: new Date().toISOString().split('T')[0],
        days_remaining: '20'
    });

    const [paymentFormData, setPaymentFormData] = useState({
        payment_date: ''
    });

    const [updateFormData, setUpdateFormData] = useState({
        opposition_status: 'pending',
        publication_date: '',
        opposition_period_start: '',
        opposition_period_end: '',
        observations: ''
    });

    const [sendEmailOnUpdate, setSendEmailOnUpdate] = useState(false);

    // Verificar si el usuario es abogado o admin
    const isLawyer = can('change_brandregistrationinapiapplication');

    // Obtener formal_examination_id al cargar
    useEffect(() => {
        if (brandRegistrationId) {
            getFormalExaminationId(brandRegistrationId, {
                onSuccess: (response: ServiceResponse) => {
                    if (response.data && response.data.formal_examination_id) {
                        setFormalExaminationId(response.data.formal_examination_id);
                    }
                },
                onError: (response: ServiceResponse) => {
                    console.log('No se pudo obtener formal_examination_id:', response.message);
                    setShowCreateForm(false);
                }
            });
        }
    }, [brandRegistrationId]);

    // Cargar datos de la publicación cuando tengamos el formal_examination_id
    useEffect(() => {
        if (formalExaminationId) {
            loadPublicationData();
        }
    }, [formalExaminationId]);

    const loadPublicationData = () => {
        if (formalExaminationId) {
            getOfficialPublication(formalExaminationId, {
                onSuccess: (response: ServiceResponse) => {
                    if (response.data && response.data.official_publication) {
                        const data = response.data.official_publication;
                        setPublicationData(data);
                        
                        // Cargar datos básicos para edición
                        setCreateFormData({
                            publication_fee: data.publication_fee?.toString() || '',
                            resolution_notification_date: data.resolution_notification_date || new Date().toISOString().split('T')[0],
                            days_remaining: data.days_remaining?.toString() || '20'
                        });

                        // Cargar fecha de pago
                        setPaymentFormData({
                            payment_date: data.payment_date || ''
                        });
                        
                        // Cargar información adicional
                        setUpdateFormData({
                            opposition_status: data.opposition_status || 'pending',
                            publication_date: data.publication_date || '',
                            opposition_period_start: data.opposition_period_start || '',
                            opposition_period_end: data.opposition_period_end || '',
                            observations: data.observations || ''
                        });
                    }
                },
                onError: () => {
                    // No hay publicación creada
                    setShowCreateForm(true);
                }
            });
        }
    };

    const handleCreateInputChange = (field: string, value: string) => {
        setCreateFormData(prev => ({ ...prev, [field]: value }));
    };

    const handleUpdateInputChange = (field: string, value: string) => {
        setUpdateFormData(prev => ({ ...prev, [field]: value }));
    };

    const handleCreatePublication = () => {
        setErrors({});

        if (!formalExaminationId) {
            toast.error('No se pudo obtener el ID del examen de forma. Por favor asegúrate de haber completado la etapa 4.');
            return;
        }

        const requestData = {
            formal_examination_id: formalExaminationId,
            publication_fee: parseFloat(createFormData.publication_fee),
            resolution_notification_date: createFormData.resolution_notification_date,
            days_remaining: parseInt(createFormData.days_remaining)
        };

        createOfficialPublication(requestData, {
            onSuccess: (response: ServiceResponse) => {
                toast.success(response.message || 'Publicación oficial creada correctamente.');
                setShowCreateForm(false);
                loadPublicationData();
                onUpdate();
            },
            onError: (response: ServiceResponse) => {
                toast.error(response.message || 'Error al crear la publicación oficial.');
            },
            onFieldError: (response: ServiceResponse) => {
                setErrors(response.data);
                toast.error('Por favor corrige los errores en el formulario.');
            }
        });
    };

    const handleSendEmail = () => {
        if (!publicationData?.id) {
            toast.error('No hay publicación para enviar email');
            return;
        }

        sendPaymentEmail(publicationData.id, {
            onSuccess: (response: ServiceResponse) => {
                toast.success(response.message || 'Email de solicitud de pago enviado correctamente.');
            },
            onError: (response: ServiceResponse) => {
                toast.error(response.message || 'Error al enviar el email.');
            }
        });
    };

    const handleInitiatePayment = () => {
        if (!publicationData?.id) {
            toast.error('No hay publicación para iniciar pago');
            return;
        }

        createFlowPayment(publicationData.id, {
            onSuccess: (response: ServiceResponse) => {
                if (response.data && response.data.payment_url) {
                    // Redirigir a Flow
                    window.location.href = safeRedirectUrl(response.data.payment_url);
                }
            },
            onError: (response: ServiceResponse) => {
                toast.error(response.message || 'Error al iniciar el pago.');
            }
        });
    };

    const handleInapiAutofill = () => {
        if (!publicationData?.id) {
            toast.error('No hay publicación para autocompletar');
            return;
        }

        getInapiDataForAutofill(publicationData.id, {
            onSuccess: (response: ServiceResponse) => {
                const data = response.data;
                
                if (!data.can_autofill) {
                    toast.warning('No se encontró información suficiente en INAPI para autocompletar');
                    return;
                }
                
                // Autocompletar los campos
                setUpdateFormData({
                    opposition_status: data.opposition_status || 'pending',
                    publication_date: data.publication_date || '',
                    opposition_period_start: data.opposition_period_start || '',
                    opposition_period_end: data.opposition_period_end || '',
                    observations: data.observations || ''
                });
                
                toast.success('Campos autocompletados con datos de INAPI');
            },
            onError: (response: ServiceResponse) => {
                toast.error(response.message || 'Error al obtener datos de INAPI');
            }
        });
    };

    const handleSavePayment = () => {
        if (!publicationData?.id) {
            toast.error('No hay publicación para actualizar');
            return;
        }

        if (!paymentFormData.payment_date) {
            toast.error('Por favor ingrese la fecha de pago');
            return;
        }

        // Actualizar con fecha de pago
        const dataToSend = {
            payment_date: paymentFormData.payment_date,
            payment_status: 'paid' // Marcar como pagado
        };

        updateOfficialPublication(publicationData.id, dataToSend, {
            onSuccess: (response: ServiceResponse) => {
                toast.success('Fecha de pago registrada correctamente');
                setShowPaymentForm(false);
                loadPublicationData();
                onUpdate(); // Actualizar el progreso de etapas
            },
            onError: (response: ServiceResponse) => {
                toast.error(response.message || 'Error al registrar el pago.');
            },
            onFieldError: (response: ServiceResponse) => {
                toast.error('Error al registrar el pago.');
            }
        });
    };

    const handleUpdatePublication = () => {
        if (!publicationData?.id) {
            toast.error('No hay publicación para actualizar');
            return;
        }

        setErrors({});

        // Agregar el parámetro send_email a los datos de actualización
        const dataToSend = {
            ...updateFormData,
            send_email: sendEmailOnUpdate
        };

        updateOfficialPublication(publicationData.id, dataToSend, {
            onSuccess: (response: ServiceResponse) => {
                const successMessage = sendEmailOnUpdate 
                    ? 'Publicación actualizada correctamente. Se ha enviado email de notificación al cliente.'
                    : 'Publicación actualizada correctamente.';
                toast.success(response.message || successMessage);
                loadPublicationData();
                setSendEmailOnUpdate(false); // Resetear el checkbox
            },
            onError: (response: ServiceResponse) => {
                toast.error(response.message || 'Error al actualizar la publicación.');
            },
            onFieldError: (response: ServiceResponse) => {
                setErrors(response.data);
                toast.error('Por favor corrige los errores en el formulario.');
            }
        });
    };

    const formatPrice = (value: number) => {
        return new Intl.NumberFormat('es-CL', {
            style: 'currency',
            currency: 'CLP'
        }).format(value);
    };

    if (fetchingGetFormalExaminationId || fetchingGetOfficialPublication) {
        return (
            <div className="text-center py-5">
                <div className="spinner-border text-primary" role="status">
                    <span className="visually-hidden">Cargando...</span>
                </div>
            </div>
        );
    }

    // Si no tenemos formal_examination_id, mostrar mensaje
    if (!formalExaminationId) {
        return (
            <div className="alert alert-info">
                <i className="fas fa-info-circle me-2"></i>
                No se encontró el examen de forma. Por favor asegúrate de haber completado la etapa 4.
            </div>
        );
    }

    // Si no hay publicación y el usuario es abogado, mostrar formulario de creación
    if (showCreateForm && isLawyer) {
        return (
            <div className="step-container">
                <div className="step-header mb-4">
                    <h4 className="">
                        <i className="fas fa-newspaper me-2 text-warning"></i>
                        Crear Publicación en Diario Oficial
                    </h4>
                    <p className="text-muted mb-0">Configure la publicación. El cliente será notificado automáticamente.</p>
                </div>

                <div className="row g-3">
                    <div className="col-md-6">
                        <InputField
                            label="Tarifa de Publicación (CLP)"
                            type="number"
                            value={createFormData.publication_fee}
                            onChange={(value: string) => handleCreateInputChange('publication_fee', value)}
                            onFocus={() => onFocusRemove('publication_fee')}
                            placeholder="150000"
                            error={fieldErrorMessage('publication_fee')}
                        />
                    </div>

                    <div className="col-md-6">
                        <InputField
                            label="Días restantes para pagar"
                            type="number"
                            value={createFormData.days_remaining}
                            onChange={(value: string) => handleCreateInputChange('days_remaining', value)}
                            onFocus={() => onFocusRemove('days_remaining')}
                            placeholder="20"
                            error={fieldErrorMessage('days_remaining')}
                        />
                    </div>

                    <div className="col-12">
                        <InputField
                            label="Fecha de notificación de resolución"
                            type="date"
                            value={createFormData.resolution_notification_date}
                            onChange={(value: string) => handleCreateInputChange('resolution_notification_date', value)}
                            onFocus={() => onFocusRemove('resolution_notification_date')}
                            error={fieldErrorMessage('resolution_notification_date')}
                        />
                    </div>
                </div>

                <div className="d-flex justify-content-end mt-4">
                    <ButtonGeneral
                        type="button"
                        variant="primary"
                        size="medium"
                        title={fetchingCreateOfficialPublication ? 'Creando...' : 'Crear Publicación'}
                        onClick={handleCreatePublication}
                        loading={fetchingCreateOfficialPublication}
                        disabled={fetchingCreateOfficialPublication}
                        icon="fas fa-plus"
                        iconPosition="left"
                    />
                </div>
            </div>
        );
    }

    // Si no hay publicación y no es abogado
    if (!publicationData) {
        return (
            <div className="step-container">
                <div className="alert alert-info">
                    <i className="fas fa-info-circle me-2"></i>
                    La publicación en el Diario Oficial aún no ha sido configurada. Por favor espere a que su abogado configure esta etapa.
                </div>
            </div>
        );
    }

    // Mostrar información de la publicación
    return (
        <div className="step-container">
            <div className="step-header mb-4">
                <h4 className="">
                    <i className="fas fa-newspaper me-2 text-warning"></i>
                    Publicación en el Diario Oficial
                </h4>
                <p className="text-muted mb-0">Gestión del pago y publicación de la marca.</p>
            </div>

            {/* Información de publicación */}
            <div className="card mb-4">
                <div className="card-header bg-light d-flex justify-content-between align-items-center">
                    <h5 className="mb-0">
                        <i className="fas fa-newspaper me-2"></i>
                        Información de Publicación
                    </h5>
                    {isEditable && !showEditBasicInfo && (
                        <ButtonGeneral
                            type="button"
                            variant="outline"
                            size="small"
                            title="Editar"
                            onClick={() => setShowEditBasicInfo(true)}
                            icon="fas fa-edit"
                            iconPosition="left"
                        />
                    )}
                </div>
                <div className="card-body">
                    <div className="alert alert-info mb-3">
                        <i className="fas fa-info-circle me-2"></i>
                        El cliente ha sido notificado sobre la publicación en el Diario Oficial. Los trámites y pagos son gestionados por el equipo legal.
                    </div>
                    
                    {!showEditBasicInfo ? (
                        // Vista de solo lectura
                    <div className="row">
                        <div className="col-md-6">
                            <p className="mb-2">
                                    <strong>Tarifa de publicación:</strong> {formatPrice(publicationData.publication_fee)}
                            </p>
                        </div>
                        <div className="col-md-6">
                            <p className="mb-2">
                                <strong>Días restantes:</strong> {publicationData.days_remaining} días
                            </p>
                            </div>
                        </div>
                    ) : (
                        // Formulario de edición
                        <div className="row g-3">
                            <div className="col-md-6">
                                <InputField
                                    label="Tarifa de Publicación (CLP)"
                                    type="number"
                                    value={createFormData.publication_fee}
                                    onChange={(value: string) => handleCreateInputChange('publication_fee', value)}
                                    onFocus={() => onFocusRemove('publication_fee')}
                                    placeholder="150000"
                                    error={fieldErrorMessage('publication_fee')}
                                />
                            </div>

                            <div className="col-md-6">
                                <InputField
                                    label="Días restantes para pagar"
                                    type="number"
                                    value={createFormData.days_remaining}
                                    onChange={(value: string) => handleCreateInputChange('days_remaining', value)}
                                    onFocus={() => onFocusRemove('days_remaining')}
                                    placeholder="20"
                                    error={fieldErrorMessage('days_remaining')}
                                />
                            </div>

                            <div className="col-12">
                                <div className="d-flex gap-2 justify-content-end">
                                    <ButtonGeneral
                                        type="button"
                                        variant="secondary"
                                        size="small"
                                        title="Cancelar"
                                        onClick={() => setShowEditBasicInfo(false)}
                                        icon="fas fa-times"
                                        iconPosition="left"
                                    />
                                    <ButtonGeneral
                                        type="button"
                                        variant="primary"
                                        size="small"
                                        title={fetchingCreateOfficialPublication ? 'Guardando...' : 'Guardar'}
                                        onClick={handleCreatePublication}
                                        loading={fetchingCreateOfficialPublication}
                                        disabled={fetchingCreateOfficialPublication}
                                        icon="fas fa-save"
                                        iconPosition="left"
                                    />
                                </div>
                            </div>
                        </div>
                            )}
                        </div>
                    </div>

            {/* Fecha de Pago del Diario Oficial */}
            <div className="card mb-4">
                <div className="card-header bg-light d-flex justify-content-between align-items-center">
                    <h5 className="mb-0">
                        <i className="fas fa-dollar-sign me-2"></i>
                        Fecha de Pago del Diario Oficial
                    </h5>
                    {isEditable && !showPaymentForm && !publicationData.payment_date && (
                        <ButtonGeneral
                            type="button"
                            variant="outline"
                            size="small"
                            title="Registrar Pago"
                            onClick={() => setShowPaymentForm(true)}
                            icon="fas fa-plus"
                            iconPosition="left"
                        />
                    )}
                    {isEditable && !showPaymentForm && publicationData.payment_date && (
                        <ButtonGeneral
                            type="button"
                            variant="outline"
                            size="small"
                            title="Editar"
                            onClick={() => setShowPaymentForm(true)}
                            icon="fas fa-edit"
                            iconPosition="left"
                        />
                    )}
                </div>
                <div className="card-body">
                    {!showPaymentForm && !publicationData.payment_date ? (
                        stagesData?.summary.payment_managed_by_firm ? (
                            <div className="alert alert-info mb-0" data-testid="payment-managed-by-firm">
                                <i className="fas fa-handshake me-2"></i>
                                {stagesData.summary.is_applicant
                                    ? 'El pago de este trámite lo acuerdas directamente con tu estudio.'
                                    : 'Pago acordado internamente con el cliente. Puedes registrar la fecha de pago como referencia.'}
                            </div>
                        ) : (
                        <div className="alert alert-warning mb-0">
                            <i className="fas fa-exclamation-triangle me-2"></i>
                            Aún no se ha registrado la fecha de pago. Por favor registre el pago para continuar a la siguiente etapa.
                        </div>
                        )
                    ) : !showPaymentForm && publicationData.payment_date ? (
                        <div className="row">
                            <div className="col-md-6">
                                <p className="mb-2">
                                    <strong>Fecha de pago:</strong> {new Date(publicationData.payment_date).toLocaleDateString('es-CL')}
                                </p>
                                <p className="mb-0">
                                    <span className="badge bg-success">
                                        <i className="fas fa-check me-1"></i>
                                        Pagado
                                    </span>
                                </p>
                            </div>
                        </div>
                    ) : (
                        <div className="row g-3">
                            <div className="col-md-6">
                                <InputField
                                    label="Fecha de Pago"
                                    type="date"
                                    value={paymentFormData.payment_date}
                                    onChange={(value: string) => setPaymentFormData({ payment_date: value })}
                                    placeholder="Seleccione la fecha"
                                />
                            </div>

                            <div className="col-12">
                                <div className="d-flex gap-2 justify-content-end">
                            <ButtonGeneral
                                type="button"
                                variant="secondary"
                                size="small"
                                        title="Cancelar"
                                        onClick={() => setShowPaymentForm(false)}
                                        icon="fas fa-times"
                                iconPosition="left"
                            />
                            <ButtonGeneral
                                type="button"
                                variant="primary"
                                        size="small"
                                        title={fetchingUpdateOfficialPublication ? 'Guardando...' : 'Guardar Pago'}
                                        onClick={handleSavePayment}
                                        loading={fetchingUpdateOfficialPublication}
                                        disabled={fetchingUpdateOfficialPublication}
                                        icon="fas fa-save"
                                iconPosition="left"
                            />
                                </div>
                            </div>
                        </div>
                        )}
                </div>
            </div>

            {/* Información Adicional (solo si ya está pagado) */}
            {publicationData.payment_date && isEditable && (
                <div className="card">
                    <div className="card-header bg-light d-flex justify-content-between align-items-center">
                        <h5 className="mb-0">
                            <i className="fas fa-edit me-2"></i>
                            Información Adicional
                        </h5>
                        <ButtonGeneral
                            type="button"
                            variant="outline"
                            size="small"
                            title={fetchingInapiAutofill ? 'Obteniendo datos...' : 'Autocompletar con INAPI'}
                            onClick={handleInapiAutofill}
                            loading={fetchingInapiAutofill}
                            disabled={fetchingInapiAutofill}
                            icon="fas fa-sync"
                            iconPosition="left"
                        />
                    </div>
                    <div className="card-body">
                        <div className="alert alert-info mb-3">
                            <i className="fas fa-info-circle me-2"></i>
                            Puedes autocompletar estos campos con datos del historial de INAPI usando el botón &ldquo;Autocompletar con INAPI&rdquo;.
                        </div>
                        
                        <div className="row g-3">
                            {/* Estado de oposición */}
                            <div className="col-12">
                                <label className="fw-bold mb-3">¿La publicación obtuvo oposición?</label>
                                <div className="row g-3">
                                    <div className="col-md-6">
                                        <div
                                            className={`square-checkbox-card ${updateFormData.opposition_status === 'no_opposition' ? 'selected-success' : ''}`}
                                            onClick={() => handleUpdateInputChange('opposition_status', 'no_opposition')}
                                            style={{ cursor: 'pointer' }}
                                        >
                                            <SquareCheckbox
                                                id="no_opposition"
                                                name="opposition_status"
                                                value="no_opposition"
                                                checked={updateFormData.opposition_status === 'no_opposition'}
                                                onChange={(value, checked) => handleUpdateInputChange('opposition_status', checked ? value : 'pending')}
                                                label="No obtuvo oposición"
                                            />
                                        </div>
                                    </div>
                                    <div className="col-md-6">
                                        <div
                                            className={`square-checkbox-card ${updateFormData.opposition_status === 'has_opposition' ? 'selected-danger' : ''}`}
                                            onClick={() => handleUpdateInputChange('opposition_status', 'has_opposition')}
                                            style={{ cursor: 'pointer' }}
                                        >
                                            <SquareCheckbox
                                                id="has_opposition"
                                                name="opposition_status"
                                                value="has_opposition"
                                                checked={updateFormData.opposition_status === 'has_opposition'}
                                                onChange={(value, checked) => handleUpdateInputChange('opposition_status', checked ? value : 'pending')}
                                                label="Sí obtuvo oposición"
                                            />
                                        </div>
                                    </div>
                                </div>
                            </div>

                            <div className="col-md-4">
                                <InputField
                                    label="Fecha de publicación"
                                    type="date"
                                    value={updateFormData.publication_date}
                                    onChange={(value: string) => handleUpdateInputChange('publication_date', value)}
                                    onFocus={() => onFocusRemove('publication_date')}
                                    error={fieldErrorMessage('publication_date')}
                                />
                            </div>

                            <div className="col-md-4">
                                <InputField
                                    label="Inicio período de oposición"
                                    type="date"
                                    value={updateFormData.opposition_period_start}
                                    onChange={(value: string) => handleUpdateInputChange('opposition_period_start', value)}
                                    onFocus={() => onFocusRemove('opposition_period_start')}
                                    error={fieldErrorMessage('opposition_period_start')}
                                />
                            </div>

                            <div className="col-md-4">
                                <InputField
                                    label="Fin período de oposición"
                                    type="date"
                                    value={updateFormData.opposition_period_end}
                                    onChange={(value: string) => handleUpdateInputChange('opposition_period_end', value)}
                                    onFocus={() => onFocusRemove('opposition_period_end')}
                                    error={fieldErrorMessage('opposition_period_end')}
                                />
                            </div>

                            <div className="col-12">
                                <TextAreaField
                                    label="Observaciones"
                                    value={updateFormData.observations}
                                    onChange={(value: string) => handleUpdateInputChange('observations', value)}
                                    onFocus={() => onFocusRemove('observations')}
                                    placeholder="Ingrese observaciones adicionales"
                                    rows={4}
                                    error={fieldErrorMessage('observations')}
                                />
                            </div>

                            <div className="col-12">
                                <div className="alert alert-secondary mb-0">
                                    <div className="form-check">
                                        <input
                                            className="form-check-input"
                                            type="checkbox"
                                            id="sendEmailCheckbox"
                                            checked={sendEmailOnUpdate}
                                            onChange={(e) => setSendEmailOnUpdate(e.target.checked)}
                                        />
                                        <label className="form-check-label" htmlFor="sendEmailCheckbox">
                                            <i className="fas fa-envelope me-2"></i>
                                            <strong>Enviar correo al cliente con la actualización de los datos</strong>
                                            <small className="d-block text-muted mt-1">
                                                El cliente recibirá un email notificando los cambios realizados en la publicación.
                                            </small>
                                        </label>
                                    </div>
                                </div>
                            </div>
                        </div>

                        <div className="d-flex justify-content-end mt-4">
                            <ButtonGeneral
                                type="button"
                                variant="primary"
                                size="medium"
                                title={fetchingUpdateOfficialPublication ? 'Guardando...' : 'Guardar Cambios'}
                                onClick={handleUpdatePublication}
                                loading={fetchingUpdateOfficialPublication}
                                disabled={fetchingUpdateOfficialPublication}
                                icon="fas fa-save"
                                iconPosition="left"
                            />
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default OfficialPublicationStep;

